const STORAGE_KEY = 'b2b:lastShopReturnContext';
const SCROLL_STORAGE_KEY = 'b2b:shopListScrollByPath';

export interface LastShopReturnContext {
    pathname: string;
    title: string;
}

export interface ShopListScrollState {
    scrollTop: number;
    elementCount: number;
}

type ShopListScrollStore = Record<string, ShopListScrollState>;

function readScrollStore(): ShopListScrollStore {
    try {
        const raw = sessionStorage.getItem(SCROLL_STORAGE_KEY);
        if (!raw) {
            return {};
        }
        const parsed = JSON.parse(raw) as unknown;
        if (!parsed || typeof parsed !== 'object') {
            return {};
        }
        return parsed as ShopListScrollStore;
    } catch {
        return {};
    }
}

function writeScrollStore(store: ShopListScrollStore): void {
    try {
        sessionStorage.setItem(SCROLL_STORAGE_KEY, JSON.stringify(store));
    } catch {
        /* quota / private mode */
    }
}

/** Update saved scroll while the list DOM is still mounted (e.g. before route change to wishlist). */
export function captureShopListScrollForPathname(pathname: string, scrollTop: number): void {
    if (!parseShopOrderCollectionFromPath(pathname) || scrollTop < 0) {
        return;
    }

    const existing = readShopListScroll(pathname);
    if (!existing || existing.elementCount <= 0) {
        return;
    }

    saveShopListScroll(pathname, scrollTop, existing.elementCount);
}

export function saveShopListScroll(
    pathname: string,
    scrollTop: number,
    elementCount: number
): void {
    if (!pathname || elementCount <= 0 || scrollTop < 0) {
        return;
    }

    const store = readScrollStore();
    store[pathname] = {
        scrollTop: Math.round(scrollTop),
        elementCount: Math.max(0, Math.floor(elementCount)),
    };
    writeScrollStore(store);
}

export function readShopListScroll(pathname: string): ShopListScrollState | null {
    if (!pathname) {
        return null;
    }

    const saved = readScrollStore()[pathname];
    if (
        !saved ||
        typeof saved.scrollTop !== 'number' ||
        typeof saved.elementCount !== 'number' ||
        saved.elementCount <= 0
    ) {
        return null;
    }

    return saved;
}

export function clearShopListScroll(pathname: string): void {
    if (!pathname) {
        return;
    }

    const store = readScrollStore();
    if (!store[pathname]) {
        return;
    }

    delete store[pathname];
    writeScrollStore(store);
}

function formatSegment(segment: string): string {
    return String(segment || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function buildShopReturnTitle(orderType: string, collection: string): string {
    return `${formatSegment(orderType)} · ${formatSegment(collection)}`;
}

export function saveLastShopReturnContext(orderType: string, collection: string): void {
    const pathname = `/shop/${orderType}/${collection}`;
    const payload: LastShopReturnContext = {
        pathname,
        title: buildShopReturnTitle(orderType, collection),
    };
    try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
        /* quota / private mode */
    }
}

export function readLastShopReturnContext(): LastShopReturnContext | null {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as unknown;
        if (
            !parsed ||
            typeof parsed !== 'object' ||
            typeof (parsed as LastShopReturnContext).pathname !== 'string' ||
            typeof (parsed as LastShopReturnContext).title !== 'string'
        ) {
            return null;
        }
        return parsed as LastShopReturnContext;
    } catch {
        return null;
    }
}

export function clearLastShopReturnContext(): void {
    try {
        sessionStorage.removeItem(STORAGE_KEY);
    } catch {
        /* ignore */
    }
}

/**
 * Parses `/shop/:orderType/:collection` (not wishlist). Useful when `match.params` is missing a segment.
 */
export function parseShopOrderCollectionFromPath(pathname: string): { orderType: string; collection: string } | null {
    if (!pathname || pathname.startsWith('/shop/wishlist/')) {
        return null;
    }
    const m = pathname.match(/^\/shop\/([^/]+)\/([^/]+)\/?$/);
    if (!m) {
        return null;
    }
    try {
        const orderType = decodeURIComponent(m[1]);
        const collection = decodeURIComponent(m[2]);
        if (!orderType || !collection) {
            return null;
        }
        return { orderType, collection };
    } catch {
        return null;
    }
}

/** Same navigation target as the wishlist header X (back to last shop or /shop). */
export function resolveWishlistShopBackTarget(): { pathname: string; ariaLabel: string } {
    const returnCtx = readLastShopReturnContext();
    const shopReturn =
        returnCtx &&
        returnCtx.pathname &&
        !returnCtx.pathname.startsWith('/shop/wishlist/') &&
        parseShopOrderCollectionFromPath(returnCtx.pathname)
            ? returnCtx
            : null;

    if (shopReturn) {
        return { pathname: shopReturn.pathname, ariaLabel: 'Go back to ' + shopReturn.title };
    }

    return { pathname: '/shop', ariaLabel: 'Back to front page' };
}

/** Leave utility pages under the shop shell (same as the header X). */
export function closeShopUtilityPage(history: { goBack: () => void; push: (path: string) => void; length?: number }): void {
    if (history.length != null && history.length > 1) {
        history.goBack();
        return;
    }

    history.push('/shop');
}

/** @deprecated Use closeShopUtilityPage */
export function closeOrderHistoryPage(history: { goBack: () => void; push: (path: string) => void; length?: number }): void {
    closeShopUtilityPage(history);
}
